import React, { useState, useEffect } from 'react';
import { Truck, Clock, Check, Loader2, AlertCircle, Package, Mail } from 'lucide-react';
import { shippingAPI } from '../utils/api';

const PRODUCTION_DAYS = 5; // Standard production time after artwork approval

const ShippingRateSelector = ({ 
  destinationZip, 
  subtotal, 
  itemCount,
  selectedRate,
  onRateSelect 
}) => {
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [lastZip, setLastZip] = useState('');

  useEffect(() => {
    // Only fetch if we have a valid 5-digit ZIP and it changed
    const cleanZip = destinationZip?.replace(/\D/g, '').slice(0, 5);
    if (cleanZip?.length === 5 && cleanZip !== lastZip) {
      fetchRates(cleanZip);
      setLastZip(cleanZip);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destinationZip, subtotal, itemCount]);

  const fetchRates = async (zip) => {
    setLoading(true);
    setError('');
    
    try {
      const response = await shippingAPI.getRates({
        destination_zip: zip,
        subtotal: subtotal || 0,
        item_count: itemCount || 1
      });
      
      if (response.success && response.rates?.length > 0) {
        setRates(response.rates);
        // Auto-select cheapest rate if none selected
        if (!selectedRate) {
          onRateSelect(response.rates[0]);
        }
      } else {
        setError('No shipping rates available for this location');
        setRates([]);
      }
    } catch (err) {
      console.error('Error fetching shipping rates:', err);
      setError('Unable to calculate shipping. Please try again.');
      setRates([]);
    } finally {
      setLoading(false);
    }
  };

  if (!destinationZip || destinationZip.replace(/\D/g, '').length < 5) {
    return (
      <div className="bg-ivory-100 rounded-xl border border-ivory-300/50 p-6">
        <div className="flex items-center gap-3 text-ivory-500">
          <Truck className="w-5 h-5" />
          <p className="font-body text-sm">Enter your ZIP code to see shipping options</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bg-ivory-100 rounded-xl border border-ivory-300/50 p-6">
        <div className="flex items-center justify-center gap-3 text-ivory-600">
          <Loader2 className="w-5 h-5 animate-spin text-gold-600" />
          <p className="font-body">Calculating shipping rates...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 rounded-xl border border-red-200 p-6">
        <div className="flex items-center gap-3 text-red-600">
          <AlertCircle className="w-5 h-5" />
          <p className="font-body text-sm">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="shipping-rate-selector">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-ivory-700 font-body flex items-center gap-2">
          <Truck className="w-4 h-4 text-gold-600" />
          Shipping Options
        </h3>
        <span className="text-xs text-ivory-500 font-body">
          Ships from Santa Ana, CA
        </span>
      </div>
      
      <div className="space-y-2">
        {rates.map((rate) => (
          <label
            key={rate.service_code}
            className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
              selectedRate?.service_code === rate.service_code
                ? 'border-gold-500 bg-gold-500/5'
                : 'border-ivory-200 bg-ivory-50 hover:border-ivory-300 hover:bg-ivory-100'
            }`}
            data-testid={`shipping-option-${rate.service_code}`}
          >
            <input
              type="radio"
              name="shipping_rate"
              value={rate.service_code}
              checked={selectedRate?.service_code === rate.service_code}
              onChange={() => onRateSelect(rate)}
              className="sr-only"
            />
            
            {/* Selection indicator */}
            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
              selectedRate?.service_code === rate.service_code
                ? 'border-gold-500 bg-gold-500'
                : 'border-ivory-300'
            }`}>
              {selectedRate?.service_code === rate.service_code && (
                <Check className="w-3 h-3 text-white" />
              )}
            </div>
            
            {/* Carrier logo/name */}
            <div className="flex-shrink-0 w-12">
              <span className={`text-xs font-bold px-2 py-1 rounded ${
                rate.carrier === 'UPS' ? 'bg-amber-100 text-amber-800' : 'bg-purple-100 text-purple-800'
              }`}>
                {rate.carrier}
              </span>
            </div>
            
            {/* Service details */}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-ivory-800 font-body">
                {rate.service_name}
                {rate.guaranteed && (
                  <span className="ml-2 text-xs text-green-600 font-normal">✓ Guaranteed</span>
                )}
              </p>
              <p className="text-xs text-ivory-500 font-body flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {rate.delivery_estimate}
              </p>
            </div>
            
            {/* Price */}
            <div className="text-right flex-shrink-0">
              <p className="text-lg font-semibold text-gold-700 font-body">
                ${rate.rate.toFixed(2)}
              </p>
              {rate.savings > 0 && (
                <p className="text-xs text-green-600 font-body">
                  Save ${rate.savings.toFixed(2)}
                </p>
              )}
            </div>
          </label>
        ))}
      </div>
      
      {/* Production Time Notice */}
      <div className="bg-ivory-100 border border-ivory-200 rounded-lg p-3 mt-3">
        <p className="text-ivory-600 text-xs font-body flex items-center gap-2">
          <Clock className="w-3 h-3 text-gold-600" />
          <span>
            <strong>Estimated delivery</strong> = {PRODUCTION_DAYS} business days production after artwork approval + shipping transit time
          </span>
        </p>
      </div>
      
      {/* Rush Production Notice */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-2">
        <p className="text-amber-700 text-xs font-body">
          <strong>Need it sooner?</strong> Rush production may be available. Contact{' '}
          <a 
            href="mailto:sales@engraveca.com" 
            className="font-semibold text-gold-700 hover:underline"
          >
            sales@engraveca.com
          </a>
          {' '}before placing your order.
        </p>
      </div>
      
      {/* Unishipper attribution */}
      <p className="text-xs text-ivory-400 text-center font-body pt-2">
        Rates provided by Unishippers • Discounted carrier rates
      </p>
    </div>
  );
};

export default ShippingRateSelector;
